import { useCallback, useEffect, useRef, useState } from "react";
import { ChatInputProps } from "./types";
import Button from "../Button";
import ButtonGroup from "../Button/Group";
import Icon from "../Icon";
import { styles } from "./styles";
import type { UploaderProps } from "../Uploader";
import { useFileDropzone } from "../Uploader/useFileDropzone";

/**
 * `Chat.Input` is a component that provides user input elements (such as textarea) to send messages in a chat interface.
 */
function ChatInput<UploadFile = File>({
  initialValue = "",
  onSubmit,
  onChange,
  onBlur,
  onFocus,
  placeholder = "Type your message...",
  disabled = false,
  uploaderProps: _uploaderProps,
  onAbort
}: ChatInputProps<UploadFile>) {
  const [value, setValue] = useState(initialValue);
  const [attachments, setAttachments] = useState<UploadFile[] | undefined>();

  const uploaderProps: Exclude<
    ChatInputProps<UploadFile>["uploaderProps"],
    true
  > = _uploaderProps === true ? {} : _uploaderProps;

  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleOnChange = (val: string) => {
    setValue(val);
    onChange?.(val);
  };

  const handleOnFileChange: UploaderProps<UploadFile>["onChange"] = useCallback(
    (successFiles, errorFiles) => {
      const fileArr = [...successFiles.values()];
      setAttachments(fileArr);
      uploaderProps?.onChange?.(successFiles, errorFiles);
      setTimeout(() => {
        textareaRef.current?.focus();
      });
    },
    [uploaderProps?.onChange]
  );

  const [fileDropzoneRef, fileDropzone] = useFileDropzone<UploadFile>({
    ...uploaderProps,
    disabled: disabled || !uploaderProps,
    onChange: handleOnFileChange
  } as UploaderProps<UploadFile>);

  const handleOnSubmit = () => {
    if (value.trim() === "" && !attachments?.length) {
      return;
    }
    onSubmit({ text: value, attachments });
    setValue("");
    setAttachments(undefined);
    fileDropzone.clearFiles();
    textareaRef.current?.focus();
  };

  const handleOnKeyDown = (e: React.KeyboardEvent) => {
    if (!disabled && e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (onAbort) {
        onAbort();
        return;
      }
      handleOnSubmit();
    }
  };

  const isSubmitDisabled =
    (disabled || (value.trim().length === 0 && !attachments?.length)) &&
    !onAbort;

  // Auto-resize textarea when value changes
  useEffect(() => {
    if (textareaRef.current) {
      // set height to auto to shrink if necessary
      textareaRef.current.style.height = "auto";
      // then set to scrollHeight to fit content
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [value]);

  return (
    <div className="kit-ChatInput" css={styles.input.base}>
      <div
        className="kit-chat-input-inner"
        ref={uploaderProps ? fileDropzoneRef : undefined}
      >
        {!!uploaderProps && (
          <>
            <div
              className="kit-chat-input-top-actions"
              css={styles.input.topActions}
            >
              <ButtonGroup justifyContent="start">
                <Button
                  prefix="Plus"
                  variant="round"
                  disabled={disabled}
                  onClick={() => {
                    fileDropzone.openFilePicker();
                  }}
                >
                  Add Files
                </Button>
              </ButtonGroup>
            </div>
            <fileDropzone.Overlay />
          </>
        )}
        <div
          className="kit-chat-input-wrapper"
          css={[styles.input.wrapper, disabled && styles.input.disabled]}
        >
          <fileDropzone.Files
            className="kit-chat-input-uploader"
            css={[styles.input.uploader]}
            listVariant="cards"
          />

          <div className="kit-chat-input-area" css={styles.input.inputArea}>
            <textarea
              aria-label="Chat input"
              css={[styles.input.textarea]}
              ref={textareaRef}
              placeholder={placeholder}
              disabled={disabled}
              value={value}
              onChange={e => handleOnChange(e.currentTarget.value)}
              onBlur={onBlur}
              onFocus={onFocus}
              onKeyDown={handleOnKeyDown}
              rows={1}
            />

            <Button
              onClick={onAbort || handleOnSubmit}
              disabled={isSubmitDisabled}
              size="small"
              ariaLabel={onAbort ? "Abort Chat Message" : "Submit Chat Message"}
              css={[
                styles.input.submitButton,
                onAbort && styles.input.abortButton
              ]}
            >
              <Icon
                name={onAbort ? "Stop" : "Arrow"}
                direction="right"
                size="medium"
              />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ChatInput;
